import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { SectionCard, StatusBadge, GradeBadge, EmptyState } from "@/components/common/Primitives";
import { StageBadge } from "@/components/common/StageBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, Search, X, ChevronLeft, ChevronRight } from "lucide-react";
import { TID } from "@/constants/testIds";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [20, 30, 50, 100];

export default function ReportsPage() {
  const nav = useNavigate();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supplierQuery, setSupplierQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    setLoading(true);
    // Fetch full history (backend returns latest-first by planned_date)
    api.get("/reports/history")
      .then(({ data }) => setAudits(data))
      .finally(() => setLoading(false));
  }, []);

  const filteredSorted = useMemo(() => {
    const q = supplierQuery.trim().toLowerCase();
    let rows = audits;
    if (q) {
      rows = audits.filter((a) => {
        const name = (a.supplier?.name || "").toLowerCase();
        const code = (a.supplier?.code || "").toLowerCase();
        return name.includes(q) || code.includes(q);
      });
    }
    // Sort by latest audit (actual_date if available, else planned_date), desc
    return rows.slice().sort((a, b) => {
      const da = a.actual_date || a.planned_date || "";
      const db = b.actual_date || b.planned_date || "";
      return db.localeCompare(da);
    });
  }, [audits, supplierQuery]);

  useEffect(() => { setPage(1); }, [supplierQuery, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageRows = filteredSorted.slice(pageStart, pageStart + pageSize);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Reports</div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Audit Reports & History</h1>
          <p className="text-sm text-slate-600 mt-1">Export audit PDFs and browse historical audit records — sorted by latest audit first.</p>
        </div>
        <div className="relative w-full md:w-80">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={supplierQuery}
            onChange={(e) => setSupplierQuery(e.target.value)}
            placeholder="Search supplier by name or code..."
            className="h-10 pl-9 pr-9"
            data-testid="report-supplier-search"
          />
          {supplierQuery && (
            <button
              onClick={() => setSupplierQuery("")}
              data-testid="report-supplier-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1 rounded"
              aria-label="Clear search"
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      <SectionCard
        title="Audit History"
        subtitle={`${filteredSorted.length} record(s)${supplierQuery ? ` matching "${supplierQuery}"` : ""} · sorted latest first`}
        actions={
          filteredSorted.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-500">Show</span>
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                <SelectTrigger data-testid="report-page-size" className="w-20 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-slate-500">per page</span>
            </div>
          )
        }
      >
        {loading ? <div className="text-sm text-slate-500">Loading...</div> :
          filteredSorted.length === 0 ? (
            <EmptyState
              title={supplierQuery ? "No matching audits" : "No audit history"}
              message={supplierQuery ? `Try a different search term or clear the filter.` : undefined}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
                      <th className="text-left px-3 py-2">Audit #</th>
                      <th className="text-left px-3 py-2">Stage</th>
                      <th className="text-left px-3 py-2">Supplier</th>
                      <th className="text-left px-3 py-2">Date ↓</th>
                      <th className="text-right px-3 py-2">Score</th>
                      <th className="text-center px-3 py-2">Grade</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-right px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((a) => (
                      <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="px-3 py-2 font-mono text-xs">{a.audit_number}</td>
                        <td className="px-3 py-2"><StageBadge stage={a.phase || a.stage || "pre_audit"} /></td>
                        <td className="px-3 py-2 font-semibold text-slate-900">{a.supplier?.name || "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs">{a.actual_date || a.planned_date}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">{a.percentage ? `${a.percentage}%` : "—"}</td>
                        <td className="px-3 py-2 text-center"><GradeBadge grade={a.grade} /></td>
                        <td className="px-3 py-2"><StatusBadge status={a.status} /></td>
                        <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                          <Button size="sm" variant="outline" onClick={() => nav(`/audits/${a.id}`)} className="h-8">Open</Button>
                          <Button size="sm" data-testid={TID.reports.exportPdfBtn(a.id)}
                            onClick={() => nav(`/reports/${a.id}`)}
                            className="h-8 bg-sams-primary hover:bg-sams-primaryHover text-white">
                            <FileDown size={12} className="mr-1" /> PDF
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination footer */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                <div className="text-xs text-slate-500">
                  Showing <span className="font-semibold text-slate-900">{pageStart + 1}</span>–
                  <span className="font-semibold text-slate-900">{Math.min(pageStart + pageSize, filteredSorted.length)}</span>
                  {" of "}<span className="font-semibold text-slate-900">{filteredSorted.length}</span>
                  {" · Page "}<span className="font-semibold text-slate-900">{currentPage}</span>{" of "}
                  <span className="font-semibold text-slate-900">{totalPages}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => setPage(1)} disabled={currentPage === 1}
                    data-testid="report-pagination-first" className="h-8 px-2 text-xs">« First</Button>
                  <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                    data-testid="report-pagination-prev" className="h-8 px-2">
                    <ChevronLeft size={13} />
                  </Button>
                  {(() => {
                    const pages = [];
                    const start = Math.max(1, currentPage - 2);
                    const end = Math.min(totalPages, start + 4);
                    for (let i = start; i <= end; i++) pages.push(i);
                    return pages.map((n) => (
                      <button key={n} onClick={() => setPage(n)} data-testid={`report-pagination-page-${n}`}
                        className={`h-8 min-w-[32px] px-2 text-xs rounded-md border transition-colors ${
                          n === currentPage
                            ? "bg-sams-primary border-sams-primary text-white font-semibold"
                            : "border-slate-200 text-slate-700 hover:bg-slate-50"
                        }`}>{n}</button>
                    ));
                  })()}
                  <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                    data-testid="report-pagination-next" className="h-8 px-2">
                    <ChevronRight size={13} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}
                    data-testid="report-pagination-last" className="h-8 px-2 text-xs">Last »</Button>
                </div>
              </div>
            </>
          )
        }
      </SectionCard>
    </div>
  );
}
